import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  CircleDashed,
  Clock3,
  FileClock,
  FileSearch,
  History,
  Info,
  RefreshCw,
  Search,
  ServerCog,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import type { PageContext } from "../App";
import { api } from "../api";
import {
  Button,
  InlineError,
  Modal,
  PageHeader,
  Panel,
  Select,
  StatePanel,
  StatusPill,
  formatDate,
} from "../components/ui";
import { useAsync } from "../hooks";
import type { AuditEvent, Job, JobStatus } from "../types";

type Tab = "jobs" | "audit";

export function JobsAuditPage({ notify }: PageContext) {
  const [tab, setTab] = useState<Tab>("jobs");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const jobs = useAsync(api.jobs, []);
  const audit = useAsync(api.audit, []);

  const hasRunning = (jobs.data ?? []).some((job) =>
    ["queued", "running", "retrying"].includes(job.status),
  );

  useEffect(() => {
    if (!hasRunning) return;
    const interval = window.setInterval(() => void jobs.reload(), 5000);
    return () => window.clearInterval(interval);
  }, [hasRunning, jobs.reload]);

  const filteredJobs = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return (jobs.data ?? []).filter(
      (job) =>
        (!status || job.status === status) &&
        (!normalized ||
          job.id.toLowerCase().includes(normalized) ||
          job.type.toLowerCase().includes(normalized) ||
          job.sellerSku?.toLowerCase().includes(normalized) ||
          job.amazonRequestId?.toLowerCase().includes(normalized)),
    );
  }, [jobs.data, query, status]);

  const filteredAudit = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return (audit.data ?? []).filter(
      (event) =>
        !normalized ||
        event.action.toLowerCase().includes(normalized) ||
        event.sellerSku?.toLowerCase().includes(normalized) ||
        event.actor?.toLowerCase().includes(normalized) ||
        event.requestId?.toLowerCase().includes(normalized),
    );
  }, [audit.data, query]);

  const runningCount = (jobs.data ?? []).filter((job) => ["running", "retrying"].includes(job.status)).length;
  const queuedCount = (jobs.data ?? []).filter((job) => job.status === "queued").length;
  const failedCount = (jobs.data ?? []).filter((job) => job.status === "failed").length;

  return (
    <div className="jobs-page">
      <PageHeader
        eyebrow="Operations"
        title="Jobs & Audit"
        description="Verfolge Amazon-Übermittlungen vom Einreihen bis zur Live-Prüfung und sieh jede bestätigte Änderung im Audit-Protokoll."
        actions={
          <Button
            variant="secondary"
            onClick={() => void Promise.all([jobs.reload(), audit.reload()])}
            icon={<RefreshCw size={16} />}
          >
            Aktualisieren
          </Button>
        }
      />

      <section className="job-metrics">
        <div>
          <span className="job-metric-icon job-metric-icon--running"><Activity size={19} /></span>
          <span><strong>{runningCount}</strong><small>Aktiv</small></span>
        </div>
        <div>
          <span className="job-metric-icon job-metric-icon--queued"><Clock3 size={19} /></span>
          <span><strong>{queuedCount}</strong><small>Eingereiht</small></span>
        </div>
        <div>
          <span className="job-metric-icon job-metric-icon--failed"><AlertTriangle size={19} /></span>
          <span><strong>{failedCount}</strong><small>Fehlgeschlagen</small></span>
        </div>
        <div className="job-metrics__polling">
          <CircleDashed className={hasRunning ? "spin" : ""} size={17} />
          <span>
            <strong>{hasRunning ? "Live-Aktualisierung aktiv" : "Keine laufenden Jobs"}</strong>
            <small>{hasRunning ? "alle 5 Sekunden" : "Stand ist aktuell"}</small>
          </span>
        </div>
      </section>

      <Panel className="operations-panel">
        <div className="operations-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={tab === "jobs"}
            className={tab === "jobs" ? "is-active" : ""}
            onClick={() => setTab("jobs")}
          >
            <ServerCog size={17} />
            Jobs
            <span>{jobs.data?.length ?? 0}</span>
          </button>
          <button
            role="tab"
            aria-selected={tab === "audit"}
            className={tab === "audit" ? "is-active" : ""}
            onClick={() => setTab("audit")}
          >
            <History size={17} />
            Audit-Protokoll
            <span>{audit.data?.length ?? 0}</span>
          </button>
        </div>

        <div className="operations-toolbar">
          <div className="matrix-search">
            <Search size={17} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={
                tab === "jobs"
                  ? "Job-ID, SKU, Typ oder Request-ID suchen …"
                  : "Aktion, SKU, Benutzer oder Request-ID suchen …"
              }
              aria-label="Operations durchsuchen"
            />
          </div>
          {tab === "jobs" && (
            <Select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              aria-label="Jobstatus filtern"
            >
              <option value="">Alle Status</option>
              <option value="queued">Eingereiht</option>
              <option value="running">Läuft</option>
              <option value="retrying">Erneuter Versuch</option>
              <option value="succeeded">Erfolgreich</option>
              <option value="failed">Fehlgeschlagen</option>
              <option value="cancelled">Abgebrochen</option>
            </Select>
          )}
        </div>

        {tab === "jobs" ? (
          jobs.error ? (
            <InlineError message={jobs.error} onRetry={() => void jobs.reload()} />
          ) : !jobs.loading && filteredJobs.length === 0 ? (
            <StatePanel
              type="empty"
              title={query || status ? "Keine passenden Jobs" : "Noch keine Amazon-Jobs"}
              description={
                query || status
                  ? "Passe Suche oder Statusfilter an."
                  : "Uploads, Rollouts, Synchronisationen und Live-Prüfungen erscheinen hier."
              }
            />
          ) : (
            <JobTable
              jobs={filteredJobs}
              loading={jobs.loading && !jobs.data}
              onSelect={setSelectedJob}
            />
          )
        ) : audit.error ? (
          <InlineError message={audit.error} onRetry={() => void audit.reload()} />
        ) : !audit.loading && filteredAudit.length === 0 ? (
          <StatePanel
            type="empty"
            title={query ? "Keine passenden Audit-Ereignisse" : "Audit-Protokoll ist leer"}
            description={
              query
                ? "Passe den Suchbegriff an."
                : "Bestätigte Schreibaktionen und Einstellungsänderungen werden hier unveränderbar protokolliert."
            }
          />
        ) : (
          <AuditTimeline events={filteredAudit} loading={audit.loading && !audit.data} />
        )}
      </Panel>

      <JobModal job={selectedJob} onClose={() => setSelectedJob(null)} />
    </div>
  );
}

function JobTable({
  jobs,
  loading,
  onSelect,
}: {
  jobs: Job[];
  loading: boolean;
  onSelect: (job: Job) => void;
}) {
  if (loading) {
    return (
      <div className="job-list job-list--loading">
        {Array.from({ length: 5 }, (_, index) => <span key={index} />)}
      </div>
    );
  }
  return (
    <div className="job-table-wrap">
      <table className="job-table">
        <thead>
          <tr><th>Job</th><th>Status</th><th>Fortschritt</th><th>Ziel</th><th>Gestartet</th><th /></tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr key={job.id ?? job.jobId} onClick={() => onSelect(job)}>
              <td>
                <span className={`job-type-icon job-type-icon--${job.status}`}>
                  <FileClock size={18} />
                </span>
                <span>
                  <strong>{humanizeJobType(job.type)}</strong>
                  <small>{job.id ?? job.jobId}</small>
                </span>
              </td>
              <td><StatusPill status={job.status} /></td>
              <td>
                <div className="job-progress">
                  <span><i style={{ width: `${progressValue(job)}%` }} /></span>
                  <small>{progressValue(job)}%</small>
                </div>
              </td>
              <td>
                <strong>{job.sellerSku ?? job.marketplace ?? "Alle Marktplätze"}</strong>
                {job.sellerSku && job.marketplace && <small>{job.marketplace}</small>}
              </td>
              <td>{formatDate(job.startedAt ?? job.createdAt)}</td>
              <td><ChevronRight size={16} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTimeline({ events, loading }: { events: AuditEvent[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="audit-timeline audit-timeline--loading">
        {Array.from({ length: 4 }, (_, index) => <span key={index} />)}
      </div>
    );
  }
  return (
    <div className="audit-timeline">
      {events.map((event) => {
        const success = !["failed", "error", "denied"].includes(
          event.outcome?.toLowerCase() ?? "",
        );
        return (
          <article key={event.id}>
            <span className={`audit-timeline__marker ${success ? "" : "is-error"}`}>
              {success ? <ShieldCheck size={16} /> : <XCircle size={16} />}
            </span>
            <div className="audit-timeline__card">
              <header>
                <div>
                  <strong>{humanizeAction(event.action)}</strong>
                  <span>
                    {event.sellerSku && <code>{event.sellerSku}</code>}
                    {event.marketplace && <small>{event.marketplace}</small>}
                  </span>
                </div>
                <time>{formatDate(event.createdAt)}</time>
              </header>
              {event.summary && <p>{event.summary}</p>}
              <footer>
                <span>Admin: {event.actor ?? "System"}</span>
                {event.requestId && <span>Request-ID <code>{event.requestId}</code></span>}
                {event.outcome && (
                  <StatusPill
                    status={success ? "success" : "error"}
                    label={event.outcome}
                  />
                )}
              </footer>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function JobModal({ job, onClose }: { job: Job | null; onClose: () => void }) {
  const detail = useAsync(
    () => (job ? api.job(job.id ?? job.jobId ?? "") : Promise.resolve(null as never)),
    [job?.id, job?.jobId],
    Boolean(job),
  );
  const current = detail.data ?? job;

  return (
    <Modal
      open={Boolean(job)}
      onClose={onClose}
      title={current ? humanizeJobType(current.type) : "Job-Detail"}
      description={current?.id ?? current?.jobId}
      size="medium"
      footer={<Button onClick={onClose}>Schließen</Button>}
    >
      {detail.error && <InlineError message={detail.error} onRetry={() => void detail.reload()} />}
      {current && (
        <div className="job-detail">
          <div className="job-detail__hero">
            <span className={`job-detail__icon job-detail__icon--${current.status}`}>
              {current.status === "succeeded" ? (
                <CheckCircle2 size={25} />
              ) : current.status === "failed" ? (
                <XCircle size={25} />
              ) : (
                <RefreshCw size={25} className={current.status === "running" ? "spin" : ""} />
              )}
            </span>
            <div>
              <StatusPill status={current.status} />
              <strong>{current.message ?? statusDescription(current.status)}</strong>
            </div>
          </div>
          <div className="job-detail__progress">
            <span><i style={{ width: `${progressValue(current)}%` }} /></span>
            <strong>{progressValue(current)}%</strong>
          </div>
          <dl>
            <div><dt>Typ</dt><dd>{humanizeJobType(current.type)}</dd></div>
            <div><dt>Seller-SKU</dt><dd>{current.sellerSku ?? "–"}</dd></div>
            <div><dt>Marketplace</dt><dd>{current.marketplace ?? "Mehrere / global"}</dd></div>
            <div><dt>Eingereiht</dt><dd>{formatDate(current.createdAt)}</dd></div>
            <div><dt>Gestartet</dt><dd>{formatDate(current.startedAt)}</dd></div>
            <div><dt>Beendet</dt><dd>{formatDate(current.finishedAt)}</dd></div>
          </dl>
          {current.result?.registryClaims && <div className="availability-rule">
            <ShieldCheck size={16} />
            <span>{Object.entries(current.result.registryClaims).map(([country, claim]) =>
              `${country}: ${claim.reconciliationRequired || claim.state === "uncertain"
                ? "Ergebnis muss zentral nachgeprüft werden; Anlage gesperrt"
                : claim.state === "applied" ? "Angebot zentral bestätigt"
                : claim.state === "submitted" ? "Amazon-Annahme gespeichert; Live-Prüfung folgt"
                : "Anlage reserviert; bei Unterbrechung Ergebnis nachprüfen"}`
            ).join(" · ")}</span>
          </div>}
          {current.amazonRequestId && (
            <div className="request-id-box">
              <FileSearch size={17} />
              <div><span>Amazon Request-ID</span><code>{current.amazonRequestId}</code></div>
            </div>
          )}
          <div className="availability-rule">
            <Info size={16} />
            <span>Ein erfolgreicher Submit-Job wird erst nach einer separaten Live-Verifikation als sichtbar bestätigt.</span>
          </div>
        </div>
      )}
    </Modal>
  );
}

function progressValue(job: Job) {
  if (job.status === "succeeded") return 100;
  if (job.status === "failed" || job.status === "cancelled") return job.progress ?? 100;
  if (job.status === "queued") return job.progress ?? 5;
  return Math.min(99, Math.max(0, job.progress ?? 50));
}

function humanizeJobType(type: string) {
  return (
    {
      full_sync: "Vollständiger Amazon-Abgleich",
      listing_submit: "Listing übermitteln",
      listing_rollout: "Länder-Rollout",
      listing_verify: "Listing live prüfen",
      media_submit: "Medien übermitteln",
      marketplace_refresh: "Marktplätze aktualisieren",
    }[type] ??
    type
      .replaceAll("_", " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
}

function humanizeAction(action: string) {
  return (
    {
      "listing.previewed": "Listing-Vorschau erzeugt",
      "listing.submitted": "Listing an Amazon gesendet",
      "listing.verified": "Live-Status geprüft",
      "rollout.submitted": "Länder-Rollout gesendet",
      "media.transformed": "Bildvariante vorbereitet",
      "media.submitted": "Medien an Amazon gesendet",
      "settings.updated": "Einstellungen geändert",
      "compliance.updated": "Compliance-Freigabe geändert",
    }[action] ??
    action
      .replaceAll(".", " · ")
      .replaceAll("_", " ")
  );
}

function statusDescription(status: JobStatus) {
  return {
    queued: "Der Job wartet auf einen freien Worker.",
    running: "Amazon-Daten werden aktuell verarbeitet.",
    retrying: "Die Prüfung wird automatisch erneut versucht.",
    succeeded: "Der Job wurde erfolgreich abgeschlossen.",
    failed: "Der Job ist fehlgeschlagen. Details stehen in der Meldung.",
    cancelled: "Der Job wurde abgebrochen.",
  }[status];
}
